import { type ReactElement, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { getRouteProjectDetails, getRouteProjects, getRouteSignIn } from "@/zapi-frontend/shared/const/router";
import { ERoleAccessType } from "@libs/shared-permissions";
import { ERoleAccess } from "../config/routeConfig";
import { useAuthenticated } from "@/zapi-frontend/shared/hooks/useAuthenticated";
// import { ImpersonationSessionWidget } from "@/frontend/components/ImpersonationThing/ImpersonationThing";
import { PageLoader } from "@/zapi-frontend/shared/ui/PageLoader";
import { useHistoricalNavigate } from "@/zapi-frontend/shared/hooks/useHistoricalNavigate";
import { Header } from "@/zapi-frontend/widgets/Header";
import { UserMenu } from "@/zapi-frontend/widgets/UserMenu";
import { CustomSnippetsBanner } from "@/zapi-frontend/widgets/CustomSnippetsBanner";
import { PinnedPromptRevisionBanner } from "@/zapi-frontend/widgets/PinnedPromptRevisionBanner";
import { ReleaseBanner } from "@/zapi-frontend/widgets/ReleaseBanner";
import { PasswordExpiryBanner } from "@/zapi-frontend/widgets/PasswordExpiryBanner/ui/PasswordExpiryBanner";
import { useFeatureFlags } from "@/zapi-frontend/shared/hooks/useFeatureFlag";

type TProtectedRouteProps = {
  children: ReactElement;
  roleAccess?: ERoleAccessType;
};

export function ProtectedRoute({ children, roleAccess }: TProtectedRouteProps) {
  const { navigate } = useHistoricalNavigate();
  const { data: isAuth, isLoading, me } = useAuthenticated();
  useFeatureFlags();
  const { pathname, search } = useLocation();
  const { id } = useParams();

  const isClientOnAdminPage = roleAccess === ERoleAccess.CLIENT_ADMIN && me?.role !== ERoleAccess.CLIENT_ADMIN;

  useEffect(() => {
    if (!isAuth && !isLoading) {
      if (pathname.includes("/project/")) {
        navigate(getRouteSignIn(), { replace: true });
        return;
      }

      const nextNav = pathname + search;
      navigate(getRouteSignIn() + `?next=${encodeURIComponent(nextNav)}`);
    }
  }, [isAuth, pathname, search, navigate, isLoading]);

  useEffect(() => {
    if (isAuth && isClientOnAdminPage) {
      navigate(getRouteProjects());
    }
  }, [isAuth, isClientOnAdminPage, navigate]);

  if (isLoading) {
    return <PageLoader className="h-svh" />;
  }

  if (!isAuth || isClientOnAdminPage) {
    return null;
  }

  const isPortfolioHome = pathname === "/";
  const isProjectPage = !!id && pathname === getRouteProjectDetails(id);

  return (
    <div
      className={
        isProjectPage || isPortfolioHome
          ? "flex min-h-svh flex-col bg-transparent"
          : "h-full bg-zinc-100 laptop:min-h-screen"
      }
    >
      <Header slot={<UserMenu />} />
      {!isPortfolioHome && <ReleaseBanner />}
      <CustomSnippetsBanner />
      <PinnedPromptRevisionBanner />
      <PasswordExpiryBanner />
      {children}
      {/*{userData?.impersonatorId ? <ImpersonationSessionWidget email={userData?.email} refetch={refetch} /> : null}*/}
    </div>
  );
}
